import type { VacationRequest, VacationRequestStatus } from './vacation.types';

export const CLOSED_REQUEST_STATUSES: readonly VacationRequestStatus[] = [
    'Rejected',
    'Completed',
];

export function isOpenVacationRequest(request: VacationRequest): boolean {
    return !CLOSED_REQUEST_STATUSES.includes(request.status);
}

/** Status pós-decisão do DP: já foi aprovada, esteja ela agendada, em andamento ou concluída. */
export const DP_APPROVED_STATUSES: readonly VacationRequestStatus[] = [
    'Scheduled',
    'OnVacation',
    'Completed',
];

export function isApprovedVacationRequest(request: VacationRequest): boolean {
    return DP_APPROVED_STATUSES.includes(request.status);
}

export function findOpenVacationRequest(requests: VacationRequest[]): VacationRequest | null {
    return requests.find(isOpenVacationRequest) ?? null;
}

export function resolveManagerEmail(request: VacationRequest | null): string | null {
    if (!request) return null;
    return request.managerApproval?.by ?? request.managerEmail ?? null;
}

/**
 * Minutos entre a criação da solicitação e sua abertura automática ao gestor.
 */
export const DRAFT_GRACE_MINUTES = 30;

const DRAFT_GRACE_MS = DRAFT_GRACE_MINUTES * 60 * 1000;

export function isDraftVacationRequest(request: VacationRequest): boolean {
    return request.status === 'Draft';
}

/**
 * Instante em que o job abre a solicitação. `createdAt` é o carimbo do documento; `requestedAt` é o
 * fallback para o intervalo em que o `serverTimestamp()` ainda não resolveu no cache local.
 */
export function resolveDraftGraceEnd(request: VacationRequest): Date | null {
    const base = request.createdAt?.toDate?.() ?? request.requestedAt?.toDate?.() ?? null;
    if (!base) return null;
    return new Date(base.getTime() + DRAFT_GRACE_MS);
}

/** Sem data confiável, considera expirado: nunca oferecer exclusão sobre um prazo que não sabemos. */
export function isDraftGraceExpired(request: VacationRequest, now: Date = new Date()): boolean {
    const end = resolveDraftGraceEnd(request);
    if (!end) return true;
    return now.getTime() >= end.getTime();
}

/**
 * A exclusão é a única desistência do solicitante e existe só dentro da carência: até aqui nenhum
 * e-mail saiu e nem gestor nem DP enxergam o pedido, então apagar não desfaz decisão de ninguém.
 * Passado o prazo, a solicitação é aberta e a saída deixa de ser do colaborador.
 */
export function canDeleteVacationRequest(
    request: VacationRequest,
    callerEmail: string | null | undefined,
    now: Date = new Date()
): boolean {
    if (!callerEmail || request.collaboratorEmail !== callerEmail) return false;
    if (!isDraftVacationRequest(request)) return false;
    return !isDraftGraceExpired(request, now);
}

/**
 * Confirmar (abrir) a solicitação é válido enquanto estiver em Draft, independente de carência.
 * Passados os 30 min, confirmar faz o que o job automático faria — cobre o caso onde o job está fora
 * do ar e o colaborador não quer esperar.
 */
export function canConfirmVacationRequest(
    request: VacationRequest,
    callerEmail: string | null | undefined
): boolean {
    if (!callerEmail) return false;
    if (callerEmail.toLowerCase() !== request.collaboratorEmail?.toLowerCase()) return false;
    return isDraftVacationRequest(request);
}
